import { useState, type DragEvent, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import { SortableList } from '../data/SortableList';
import { FieldMenu, type FieldMenuAction } from './FieldMenu';
import { FIELD_DRAG_TYPE } from './FieldList';
import { SHELF_FOR_KEY, visuallyHidden, type ShelfId } from './fields';

export const TOKEN_DRAG_TYPE = 'application/x-eis-token';

/** One token on a shelf. */
export interface ShelfItem {
  readonly key: string;
  /** What the token says: "Police Status", "SUMME Prämie Gesamtsumme". */
  readonly label: string;
  /** A second line: „Prämie / Monat“ · €, "≥ 01.01.2026". */
  readonly detail?: string;
  /** A small marker: "fest" / "änderbar" on a filter, "MONAT" on a bucketed axis. */
  readonly tag?: string;
}

export type ShelfDrop =
  | { readonly kind: 'field'; readonly field: string }
  | { readonly kind: 'token'; readonly from: ShelfId; readonly key: string };

export interface FieldShelfProps {
  readonly shelf: ShelfId;
  /** The shelf's title: "Zeilen", "X-Achse (Zeilen)". */
  readonly label: string;
  readonly items: readonly ShelfItem[];
  /** P-1's limit for this shelf: the counter shows "2 / 3" and a full shelf refuses a drop. */
  readonly max: number;
  readonly onDrop: (drop: ShelfDrop) => void;
  readonly onMoveTo: (key: string, to: ShelfId) => void;
  readonly onReorder: (from: number, to: number) => void;
  readonly onRemove: (key: string) => void;
  /** Display actions of a token's menu: title override, format, aggregation, bucket. */
  readonly displayActions?: (item: ShelfItem) => readonly FieldMenuAction[];
  /** The other shelves a token may move to. */
  readonly targets?: readonly ShelfId[];
}

/**
 * One shelf of the report editor (SDD-024 U-1…U-3; mockup boards 4–6): Filter, Zeilen, Spalten or
 * Werte. Fields and tokens are dropped onto it with native HTML5 drag events (no dependency); a
 * token's `⋮` menu (Enter, Shift+F10) moves it to another shelf (R / C / V / F), up and down
 * (Alt+↑ / Alt+↓ — `SortableList`'s keyboard twin of the drag) or off the shelf (Del), and offers
 * the display actions. The counter shows the limit; the caller announces every move in its live
 * region.
 */
export function FieldShelf({
  shelf,
  label,
  items,
  max,
  onDrop,
  onMoveTo,
  onReorder,
  onRemove,
  displayActions,
  targets = ['rows', 'columns', 'values', 'filters'],
}: FieldShelfProps): ReactNode {
  const { t } = useTranslation('patterns');
  const [over, setOver] = useState(false);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; item: ShelfItem; index: number } | null>(
    null,
  );
  const full = items.length >= max;
  const accepts = (event: DragEvent): boolean => {
    const types = [...event.dataTransfer.types];
    return types.includes(FIELD_DRAG_TYPE) || types.includes(TOKEN_DRAG_TYPE);
  };
  const moveTargets = targets.filter((s) => s !== shelf);

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label={label}
      data-shelf={shelf}
      onDragOver={(event) => {
        if (!accepts(event)) return;
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => {
        setOver(false);
      }}
      onDrop={(event) => {
        setOver(false);
        const field = event.dataTransfer.getData(FIELD_DRAG_TYPE);
        const token = event.dataTransfer.getData(TOKEN_DRAG_TYPE);
        if (field !== '') {
          event.preventDefault();
          onDrop({ kind: 'field', field });
        } else if (token !== '') {
          const [from, key] = token.split('|');
          if (from !== undefined && key !== undefined && from !== shelf) {
            event.preventDefault();
            onDrop({ kind: 'token', from: from as ShelfId, key });
          }
        }
      }}
      sx={{
        p: 1,
        borderStyle: over ? 'dashed' : 'solid',
        borderColor: over ? 'primary.main' : 'divider',
        bgcolor: over ? 'action.hover' : 'background.paper',
      }}
    >
      <Stack
        direction="row"
        sx={{ alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}
      >
        <Typography variant="subtitle2" component="h3">
          {label}
        </Typography>
        <Typography
          variant="caption"
          color={full ? 'warning.main' : 'text.secondary'}
          data-testid={`shelf-counter-${shelf}`}
        >
          {t('reporting.shelf.counter', { count: items.length, max })}
        </Typography>
      </Stack>
      {items.length > 0 && (
        <SortableList
          items={items}
          label={label}
          itemKey={(item) => item.key}
          itemLabel={(item) => item.label}
          onMove={onReorder}
          hideMoveButtons
          dragData={(item) => [TOKEN_DRAG_TYPE, `${shelf}|${item.key}`]}
          renderItem={(item, index) => (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', minWidth: 0 }}>
              <Box
                role="button"
                tabIndex={0}
                aria-haspopup="menu"
                aria-label={
                  item.detail === undefined ? item.label : `${item.label}, ${item.detail}`
                }
                data-token={item.key}
                onClick={(event) => {
                  setMenu({ anchor: event.currentTarget, item, index });
                }}
                onKeyDown={(event) => {
                  if (event.altKey) return; // Alt+↑/↓: SortableList moves the row
                  if (
                    event.key === 'Enter' ||
                    event.key === ' ' ||
                    (event.key === 'F10' && event.shiftKey)
                  ) {
                    event.preventDefault();
                    setMenu({ anchor: event.currentTarget, item, index });
                  } else if (event.key === 'Delete' || event.key === 'Backspace') {
                    event.preventDefault();
                    onRemove(item.key);
                  } else {
                    const to = SHELF_FOR_KEY[event.key.toLowerCase()];
                    if (to !== undefined && to !== shelf && moveTargets.includes(to)) {
                      event.preventDefault();
                      onMoveTo(item.key, to);
                    }
                  }
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  cursor: 'pointer',
                  borderRadius: 0.5,
                  '&:focus-visible': {
                    outline: 2,
                    outlineStyle: 'solid',
                    outlineColor: 'primary.main',
                  },
                }}
              >
                <Typography variant="body2" noWrap>
                  {item.label}
                </Typography>
                {item.detail !== undefined && (
                  <Typography variant="caption" color="text.secondary" noWrap component="p">
                    {item.detail}
                  </Typography>
                )}
              </Box>
              {item.tag !== undefined && (
                <Chip size="small" label={item.tag} variant="outlined" sx={{ height: 20 }} />
              )}
              <IconButton
                size="small"
                aria-label={t('reporting.shelf.menu', { item: item.label })}
                aria-haspopup="menu"
                tabIndex={-1}
                onClick={(event) => {
                  setMenu({ anchor: event.currentTarget, item, index });
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        />
      )}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: 'block',
          px: 1,
          py: 0.5,
          border: 1,
          borderStyle: 'dashed',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {full ? t('reporting.shelf.full', { max }) : t('reporting.shelf.dropHere')}
      </Typography>
      <Box sx={visuallyHidden}>{label}</Box>
      <FieldMenu
        anchorEl={menu?.anchor ?? null}
        open={menu !== null}
        label={t('reporting.shelf.menu', { item: menu?.item.label ?? '' })}
        onClose={() => {
          setMenu(null);
        }}
        moveTargets={moveTargets}
        onMove={(to) => {
          if (menu !== null) onMoveTo(menu.item.key, to);
        }}
        within={
          menu === null
            ? []
            : [
                {
                  label: t('reporting.shelf.up'),
                  shortcut: 'Alt+↑',
                  disabled: menu.index === 0,
                  onClick: () => {
                    onReorder(menu.index, menu.index - 1);
                  },
                },
                {
                  label: t('reporting.shelf.down'),
                  shortcut: 'Alt+↓',
                  disabled: menu.index === items.length - 1,
                  onClick: () => {
                    onReorder(menu.index, menu.index + 1);
                  },
                },
                {
                  label: t('reporting.shelf.remove'),
                  shortcut: 'Del',
                  onClick: () => {
                    onRemove(menu.item.key);
                  },
                },
              ]
        }
        display={menu === null ? [] : (displayActions?.(menu.item) ?? [])}
      />
    </Paper>
  );
}

/** The editor's live region (U-2): every move is announced, politely. */
export function LiveRegion({ message }: { readonly message: string }): ReactNode {
  return (
    <Box role="status" aria-live="polite" sx={visuallyHidden} data-testid="editor-live-region">
      {message}
    </Box>
  );
}
